import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Avatar,
  Badge,
  Tooltip,
  Breadcrumbs,
  Link,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Add, 
  Refresh, 
  AccessTime, 
  CalendarToday, 
  Info,
  NavigateNext,
  Home,
  FilterList,
  MoreVert,
  CheckCircle,
  Cancel,
  Visibility,
  ContentCopy,
  Settings,
  LocalShipping,
  Schedule,
  RocketLaunch,
  Walk,
  Bike,
  Car,
  Truck,
  Close,
  StarBorder,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListDeliveryModelsQuery,
  useAddDeliveryModelMutation,
  useUpdateDeliveryModelMutation,
  useDeleteDeliveryModelMutation,
} from '../../features/admin/deliveryApi.ts';

import {
  DeliveryModel,
  DeliveryModelFormData,
  DELIVERY_TYPE_OPTIONS,
  STATUS_OPTIONS,
  IS_DEFAULT_OPTIONS,
  IS_RESTRICTED_OPTIONS,
} from '../../features/admin/types.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
};

const DeliveryManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<DeliveryModel | null>(null);
  const [formData, setFormData] = useState<DeliveryModelFormData>({
    model_name: '',
    delivery_type: 0,
    hours: 1,
    days: 1,
    display_text: '',
    status: 1,
    is_default: 0,
    is_restricted: 1,
    delivery_charge: 'FREE',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Local state for delivery models
  const [localDeliveryModels, setLocalDeliveryModels] = useState<DeliveryModel[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessingDefault, setIsProcessingDefault] = useState<number | null>(null);

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListDeliveryModelsQuery();

  const [addDeliveryModel, { isLoading: isAdding }] = useAddDeliveryModelMutation();
  const [updateDeliveryModel, { isLoading: isUpdating }] = useUpdateDeliveryModelMutation();
  const [deleteDeliveryModel, { isLoading: isDeleting }] = useDeleteDeliveryModelMutation();

  // Sort models with default first
  const sortModels = (models: DeliveryModel[]) => {
    return [...models].sort((a, b) => {
      // Default models first
      if (a.is_default === 1 && b.is_default !== 1) return -1;
      if (a.is_default !== 1 && b.is_default === 1) return 1;
      
      // Then sort by name
      const nameA = a.model_name || '';
      const nameB = b.model_name || '';
      return nameA.localeCompare(nameB);
    });
  };

  // Sync API data with local state
  useEffect(() => {
    if (data && !isLoading) {
      const models = data.result || data.data || [];
      console.log('API Data Received:', models);
      
      // Ensure proper mapping of fields
      const mappedModels = models.map((model: DeliveryModel) => ({
        ...model,
        id: model.id || model.model_id,
        model_id: model.model_id || model.id,
        delivery_charge: model.delivery_charge || 'FREE',
      }));
      
      const sortedModels = sortModels(mappedModels);
      setLocalDeliveryModels(sortedModels);
      setIsRefreshing(false);
      setIsProcessingDefault(null);
    }
  }, [data, isLoading]);

  const isEditing = Boolean(editingModel);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      model_name: '',
      delivery_type: 0,
      hours: 1,
      days: 1,
      display_text: '',
      status: 1,
      is_default: 0,
      is_restricted: 1,
      delivery_charge: 'FREE',
    });
    setFormErrors({});
    setEditingModel(null);
  };

  const handleAddModel = () => {
    resetForm();
    // Pre-check default if there's no existing default
    const hasDefault = localDeliveryModels.some(model => model.is_default === 1);
    setFormData(prev => ({
      ...prev,
      is_default: !hasDefault ? 1 : 0
    }));
    setFormOpen(true);
  };

  const handleEditModel = (model: DeliveryModel) => {
    console.log('Editing model:', model);
    setFormData({
      model_name: model.model_name || '',
      delivery_type: model.delivery_type || 0,
      hours: model.hours === null ? 1 : (model.hours || 1),
      days: model.days === null ? 1 : (model.days || 1),
      display_text: model.display_text || '',
      status: model.status || 1,
      is_default: model.is_default || 0,
      is_restricted: model.is_restricted || 1,
      delivery_charge: model.delivery_charge || 'FREE',
      model_id: model.model_id || model.id,
    });
    setEditingModel(model);
    setFormOpen(true);
  };

  const handleSetDefault = async (model: DeliveryModel) => {
    const modelId = model.model_id || model.id;
    
    if (model.is_default === 1) {
      setSnackbar({
        open: true,
        message: 'This delivery option is already the default.',
        severity: 'info'
      });
      return;
    }

    try {
      setIsProcessingDefault(modelId);
      
      // Find the current default model
      const currentDefaultModel = localDeliveryModels.find(m => m.is_default === 1);
      
      // Create batch update data
      const updatePromises = [];
      
      // If there's a current default, prepare to unset it
      if (currentDefaultModel) {
        const currentDefaultId = currentDefaultModel.model_id || currentDefaultModel.id;
        if (currentDefaultId !== modelId) { // Ensure we're not updating the same model twice
          const unsetData = {
            model_id: currentDefaultId,
            model_name: currentDefaultModel.model_name,
            delivery_type: currentDefaultModel.delivery_type,
            hours: currentDefaultModel.hours,
            days: currentDefaultModel.days,
            display_text: currentDefaultModel.display_text,
            status: currentDefaultModel.status,
            is_default: 0, // Unset default
            is_restricted: currentDefaultModel.is_restricted,
            delivery_charge: currentDefaultModel.delivery_charge || 'FREE',
          };
          
          updatePromises.push(updateDeliveryModel(unsetData).unwrap());
        }
      }
      
      // Prepare data for setting new default
      const setDefaultData = {
        model_id: modelId,
        model_name: model.model_name,
        delivery_type: model.delivery_type,
        hours: model.hours,
        days: model.days,
        display_text: model.display_text,
        status: model.status,
        is_default: 1,
        is_restricted: model.is_restricted,
        delivery_charge: model.delivery_charge || 'FREE',
      };
      
      updatePromises.push(updateDeliveryModel(setDefaultData).unwrap());
      
      // Update local state immediately for better UX
      setLocalDeliveryModels(prev => {
        const updatedModels = prev.map(m => {
          const currentModelId = m.model_id || m.id;
          
          // Set this model as default
          if (currentModelId === modelId) {
            return { ...m, is_default: 1 };
          }
          // Unset all other defaults
          else if (m.is_default === 1) {
            return { ...m, is_default: 0 };
          }
          return m;
        });
        
        return sortModels(updatedModels);
      });
      
      // Execute all updates
      await Promise.all(updatePromises);
      
      // Re-fetch to ensure everything is in sync
      await refetch();
      
      setSnackbar({
        open: true,
        message: `${model.model_name} is now the default delivery option!`,
        severity: 'success'
      });
      
    } catch (err: any) {
      console.error('Failed to set default delivery model:', err);
      
      // Revert local state changes on error
      await refetch();
      
      let errorMessage = 'Failed to set default delivery option. Please try again.';
      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsProcessingDefault(null);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
    setFormErrors({}); // Clear any form errors
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));

    // Clear error when user makes selection
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.model_name.trim()) {
      newErrors.model_name = 'Delivery name is required';
    }

    if (!formData.display_text.trim()) {
      newErrors.display_text = 'Display text is required';
    }

    if (formData.delivery_type === 0 && (!formData.hours || formData.hours < 1)) {
      newErrors.hours = 'Hours must be at least 1';
    }

    if (formData.delivery_type === 1 && (!formData.days || formData.days < 1)) {
      newErrors.days = 'Days must be at least 1';
    }

    if (!formData.delivery_charge?.trim()) {
      newErrors.delivery_charge = 'Delivery charge is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const modelId = formData.model_id || editingModel?.model_id || editingModel?.id;
      
      if (isEditing && editingModel && modelId) {
        const updateData = {
          model_id: modelId,
          model_name: formData.model_name.trim(),
          delivery_type: formData.delivery_type,
          hours: formData.hours,
          days: formData.days,
          display_text: formData.display_text.trim(),
          status: formData.status,
          is_default: formData.is_default,
          is_restricted: formData.is_restricted,
          delivery_charge: formData.delivery_charge?.trim() || 'FREE',
        };
        
        console.log('Updating delivery model:', updateData);
        
        // If setting as default
        if (formData.is_default === 1) {
          setIsProcessingDefault(modelId);
          
          // Update local state immediately
          setLocalDeliveryModels(prev => {
            const updatedModels = prev.map(model => {
              const currentModelId = model.model_id || model.id;
              
              // Set the new model as default
              if (currentModelId === modelId) {
                return { 
                  ...model, 
                  ...updateData,
                  model_id: modelId,
                  id: modelId,
                  is_default: 1
                };
              }
              // Unset all other defaults
              else if (model.is_default === 1) {
                return { ...model, is_default: 0 };
              }
              return model;
            });
            
            return sortModels(updatedModels);
          });
          
          handleFormClose();
          
          const result = await updateDeliveryModel(updateData).unwrap();
          console.log('Update response:', result);
          
          setSnackbar({
            open: true,
            message: result?.message || 'Delivery option updated and set as default successfully!',
            severity: 'success'
          });
          
        } else {
          // Not setting as default - normal update
          setLocalDeliveryModels(prev => {
            const updatedModels = prev.map(model => {
              const currentModelId = model.model_id || model.id;
              if (currentModelId === modelId) {
                return { 
                  ...model, 
                  ...updateData,
                  model_id: modelId,
                  id: modelId
                };
              }
              return model;
            });
            
            return sortModels(updatedModels);
          });
          
          handleFormClose();
          
          const result = await updateDeliveryModel(updateData).unwrap();
          console.log('Update response:', result);
          
          setSnackbar({
            open: true,
            message: result?.message || 'Delivery option updated successfully!',
            severity: 'success'
          });
        }
        
        // Refetch to ensure sync
        refetch();
        
      } else {
        // Creating new model
        const addData = {
          model_name: formData.model_name.trim(),
          delivery_type: formData.delivery_type,
          hours: formData.hours,
          days: formData.days,
          display_text: formData.display_text.trim(),
          status: formData.status,
          is_default: formData.is_default,
          is_restricted: formData.is_restricted,
          delivery_charge: formData.delivery_charge?.trim() || 'FREE',
        };
        
        console.log('Creating delivery model:', addData);
        
        // If setting as default
        if (formData.is_default === 1) {
          // Update local state immediately
          setLocalDeliveryModels(prev => {
            const updatedModels = prev.map(model => ({
              ...model,
              is_default: 0
            }));
            
            // Add new model temporarily
            const tempId = Date.now();
            const tempModel: DeliveryModel = {
              id: tempId,
              model_id: tempId,
              ...addData,
              createddt: new Date().toISOString(),
              updateddt: null,
            };
            
            updatedModels.push(tempModel);
            return sortModels(updatedModels);
          });
          
          handleFormClose();
          
          const result = await addDeliveryModel(addData).unwrap();
          console.log('Create response:', result);
          
          setSnackbar({
            open: true,
            message: result?.message || 'Delivery option created and set as default successfully!',
            severity: 'success'
          });
          
          // Refetch
          refetch();
          
        } else {
          // Not setting as default
          const tempId = Date.now();
          const tempModel: DeliveryModel = {
            id: tempId,
            model_id: tempId,
            ...addData,
            createddt: new Date().toISOString(),
            updateddt: null,
          };
          
          setLocalDeliveryModels(prev => {
            const newModels = [...prev, tempModel];
            return sortModels(newModels);
          });
          
          handleFormClose();
          
          const result = await addDeliveryModel(addData).unwrap();
          console.log('Create response:', result);
          
          setSnackbar({
            open: true,
            message: result?.message || 'Delivery option created successfully!',
            severity: 'success'
          });
          
          // Refetch
          refetch();
        }
      }
      
    } catch (err: any) {
      console.error('Failed to save delivery model:', err);
      setIsProcessingDefault(null);

      let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} delivery option. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      // Refetch to restore correct state
      refetch();
    }
  };

  const handleDelete = async (id: number) => {
    const modelToDelete = localDeliveryModels.find(model => (model.model_id || model.id) === id);
    
    if (!modelToDelete) return;
    
    // Prevent deletion if it's the default option
    if (modelToDelete.is_default === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot delete the default delivery option. Please set another option as default first.',
        severity: 'error'
      });
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this delivery option?')) {
      return;
    }

    try {
      // Optimistically remove from local state
      setLocalDeliveryModels(prev => 
        prev.filter(model => (model.model_id || model.id) !== id)
      );
      
      const result = await deleteDeliveryModel({ model_id: id }).unwrap();
      
      setSnackbar({
        open: true,
        message: result?.message || 'Delivery option deleted successfully!',
        severity: 'success'
      });
      
      // Refetch to sync
      refetch();
      
    } catch (err: any) {
      console.error('Failed to delete delivery model:', err);

      let errorMessage = 'Error deleting delivery option. Please try again.';

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      // Restore state by refetching
      refetch();
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch().finally(() => {
      setIsRefreshing(false);
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view delivery options.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No delivery options found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load delivery options. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Shopify-style status chip
  const getStatusChip = (status: number) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return (
      <Chip
        label={statusOption?.label || 'Unknown'}
        size="small"
        variant="outlined"
        sx={{
          borderRadius: '4px',
          fontWeight: 500,
          borderColor: status === 1 ? shopifyColors.success : shopifyColors.textSecondary,
          color: status === 1 ? shopifyColors.success : shopifyColors.textSecondary,
          backgroundColor: status === 1 ? `${shopifyColors.success}08` : 'transparent',
        }}
      />
    );
  };

  // Shopify-style delivery type display
  const getDeliveryTypeDisplay = (deliveryType: number, hours: number | null | undefined, days: number | null | undefined) => {
    const getIcon = () => {
      switch (deliveryType) {
        case 0: // Hour-based
          return <RocketLaunch sx={{ color: shopifyColors.blue, fontSize: 16 }} />;
        case 1: // Day-based
          return <Schedule sx={{ color: shopifyColors.purple, fontSize: 16 }} />;
        default:
          return <LocalShipping sx={{ color: shopifyColors.textSecondary, fontSize: 16 }} />;
      }
    };

    const getText = () => {
      if (deliveryType === 0) {
        const hoursValue = hours || 0;
        return `${hoursValue} hour${hoursValue > 1 ? 's' : ''}`;
      } else {
        const daysValue = days || 0;
        return `${daysValue} day${daysValue > 1 ? 's' : ''}`;
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon()}
        <Typography variant="body2" sx={{ color: shopifyColors.textPrimary, fontWeight: 500 }}>
          {getText()}
        </Typography>
      </Box>
    );
  };

  // Get avatar color based on delivery type
  const getAvatarColor = (deliveryType: number) => {
    switch (deliveryType) {
      case 0: return shopifyColors.blue;
      case 1: return shopifyColors.purple;
      default: return shopifyColors.primary;
    }
  };

  // Get delivery type icon
  const getDeliveryTypeIcon = (deliveryType: number) => {
    switch (deliveryType) {
      case 0: return <RocketLaunch />;
      case 1: return <Schedule />;
      default: return <LocalShipping />;
    }
  };

  // Format delivery charge display
  const formatDeliveryCharge = (charge: string) => {
    if (!charge || charge === 'FREE') return 'FREE';
    if (charge === '0') return 'FREE';
    if (charge === '0.00') return 'FREE';
    return `$${parseFloat(charge).toFixed(2)}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Shopify-style Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Settings
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Delivery options
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Delivery options
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage how quickly customers receive their orders
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddModel}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Create delivery option
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2 }}>
            {/* Search and Filters */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    More filters
                  </Button>
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading || isRefreshing}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && localDeliveryModels.length > 0 && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {localDeliveryModels.length} delivery option{localDeliveryModels.length === 1 ? '' : 's'}
              </Typography>
            )}

            {/* Loading State */}
            {(isLoading || isRefreshing) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>
                  {isRefreshing ? 'Refreshing delivery options...' : 'Loading delivery options...'}
                </Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && localDeliveryModels.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <LocalShipping sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  No delivery options yet
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  Create your first delivery option to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddModel}
                  sx={{
                    backgroundColor: shopifyColors.primary,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: shopifyColors.primaryLight,
                    },
                  }}
                >
                  Create delivery option
                </Button>
              </Card>
            )}

            {/* Delivery Options List */}
            {!isLoading && localDeliveryModels.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {localDeliveryModels.map((model: DeliveryModel, index: number) => {
                    const modelId = model.model_id || model.id;
                    const isDefault = model.is_default === 1;
                    const isProcessing = isProcessingDefault === modelId;
                    
                    return (
                      <ListItem
                        key={`delivery-${modelId}`}
                        sx={{
                          borderBottom: index < localDeliveryModels.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                          '&:hover': {
                            backgroundColor: `${shopifyColors.background}80`,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              bgcolor: getAvatarColor(model.delivery_type),
                              width: 40,
                              height: 40,
                            }}
                          >
                            {getDeliveryTypeIcon(model.delivery_type)}
                          </Avatar>
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                                {model.model_name || 'Unnamed'}
                              </Typography>
                              {isDefault && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  sx={{
                                    backgroundColor: shopifyColors.warning,
                                    color: '#000',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                              {getStatusChip(model.status)}
                              {isProcessing && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              {getDeliveryTypeDisplay(model.delivery_type, model.hours, model.days)}
                              {model.display_text && (
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  {model.display_text}
                                </Typography>
                              )}
                              <Chip 
                                label={formatDeliveryCharge(model.delivery_charge || 'FREE')} 
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? shopifyColors.success : shopifyColors.primary,
                                  color: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? shopifyColors.success : shopifyColors.primary,
                                  backgroundColor: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? `${shopifyColors.success}08` : `${shopifyColors.primary}08`,
                                  fontWeight: 500,
                                }}
                              />
                            </Box>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {/* Set as Default button - only show if not already default */}
                            {!isDefault && (
                              <Tooltip title="Set as Default">
                                <IconButton
                                  onClick={() => handleSetDefault(model)}
                                  size="small"
                                  disabled={isProcessing}
                                  sx={{
                                    color: shopifyColors.warning,
                                    '&:hover': {
                                      backgroundColor: shopifyColors.warning + '20',
                                    }
                                  }}
                                >
                                  <StarBorder />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleEditModel(model)}
                                size="small"
                                disabled={isProcessing}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title={isDefault ? "Cannot delete default option" : "Delete"}>
                              <span>
                                <IconButton
                                  onClick={() => handleDelete(modelId)}
                                  disabled={isDeleting || isDefault || isProcessing}
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Shopify-style Create/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit delivery option' : 'Create delivery option'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 1 
                }}>
                  Basic information
                </Typography>
                
                <TextField
                  name="model_name"
                  label="Delivery name"
                  value={formData.model_name}
                  onChange={handleInputChange}
                  error={!!formErrors.model_name}
                  helperText={formErrors.model_name}
                  fullWidth
                  required
                  placeholder="e.g., Express delivery, Standard shipping"
                  disabled={isFormLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Delivery Type Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Delivery speed
                </Typography>
                
                <FormControl fullWidth required>
                  <InputLabel>Delivery type</InputLabel>
                  <Select
                    name="delivery_type"
                    value={formData.delivery_type}
                    onChange={handleSelectChange}
                    label="Delivery type"
                    disabled={isFormLoading}
                    sx={{ borderRadius: '6px' }}
                  >
                    {DELIVERY_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {option.value === 0 ? (
                            <RocketLaunch sx={{ color: shopifyColors.blue }} />
                          ) : (
                            <Schedule sx={{ color: shopifyColors.purple }} />
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Delivery Time and Charge */}
              <Grid container spacing={2}>
                {formData.delivery_type === 0 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        name="hours"
                        label="Maximum hours"
                        type="number"
                        value={formData.hours}
                        onChange={handleInputChange}
                        error={!!formErrors.hours}
                        helperText={formErrors.hours || "Maximum hours for delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        inputProps={{ min: 1, max: 168 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="delivery_charge"
                        label="Delivery charge"
                        value={formData.delivery_charge}
                        onChange={handleInputChange}
                        error={!!formErrors.delivery_charge}
                        helperText={formErrors.delivery_charge || "Enter 0 for FREE delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        placeholder="0.00"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                {formData.delivery_type === 1 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        name="days"
                        label="Maximum days"
                        type="number"
                        value={formData.days}
                        onChange={handleInputChange}
                        error={!!formErrors.days}
                        helperText={formErrors.days || "Maximum days for delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        inputProps={{ min: 1, max: 365 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="delivery_charge"
                        label="Delivery charge"
                        value={formData.delivery_charge}
                        onChange={handleInputChange}
                        error={!!formErrors.delivery_charge}
                        helperText={formErrors.delivery_charge || "Enter 0 for FREE delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        placeholder="0.00"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Display Text */}
              <Box>
                <TextField
                  name="display_text"
                  label="Customer display text"
                  value={formData.display_text}
                  onChange={handleInputChange}
                  error={!!formErrors.display_text}
                  helperText={formErrors.display_text || "This text will be shown to customers at checkout"}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  disabled={isFormLoading}
                  placeholder="e.g., Get it in 2 hours, Standard 3-5 business days"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Settings */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Settings
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          status: e.target.checked ? 1 : 0 
                        }))}
                        color="success"
                        disabled={isFormLoading}
                      />
                    }
                    label="Active"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          is_default: e.target.checked ? 1 : 0 
                        }))}
                        color="warning"
                        disabled={isFormLoading}
                      />
                    }
                    label="Set as default delivery option"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Customer access</InputLabel>
                    <Select
                      name="is_restricted"
                      value={formData.is_restricted}
                      onChange={handleSelectChange}
                      label="Customer access"
                      disabled={isFormLoading}
                      sx={{ borderRadius: '6px' }}
                    >
                      {IS_RESTRICTED_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Choose who can use this delivery option</FormHelperText>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel  
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.model_name?.trim() || !formData.display_text?.trim() || !formData.delivery_charge?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Create delivery option'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryManagement;